import React, { useContext, useMemo } from "react";

import { useAppContext } from "../hooks/useAppContext";
import { useEditor } from "../hooks/useEditor";
import { useErrorHandling } from "../hooks/useErrorHandling";
import { useFeatureFlagToggle } from "../hooks/useFeatureFlags";

type IImporterContext = eXpress.client.PhotoImporter | undefined;

// Create Context
const ImporterContext = React.createContext<IImporterContext>({} as IImporterContext);
ImporterContext.displayName = "ImporterContext";

/**
 * Component that provides a importer (for downloading/uploading photos)
 */
export function ImporterContextProvider(
	props: React.PropsWithChildren<{
		context?: IImporterContext;
	}>,
) {
	const { children, context: initialContext } = props;

	const editor = useEditor();
	const { logger, client } = editor;
	const {
		platform: { queryString, webWorker },
		context: { revision = "1", jsUrl },
		networkMonitor,
	} = useAppContext();
	const { createFatalErrorHandler } = useErrorHandling();
	const disableImport = useFeatureFlagToggle("debugDisableImport");

	const contextValue = useMemo(
		() =>
			initialContext ||
			(disableImport
				? undefined
				: new eXpress.client.PhotoImporter(editor, {
						client,
						downloadClient: client,
						uploadClient: client,
						fatalErrorHandlerCreator: () => createFatalErrorHandler(),
						createErrorHandler: () => createFatalErrorHandler(),
						getPhotoDictionary: () => editor.assets.photos, // Don't destructure assets from editor, assets are only available after definition is loaded
						queryString,
						makeUploadedPhotoSourcesAsync:
							client.makeUploadedPhotoSourcesAsync.bind(client),
						logger,
						webWorker,
						revision,
						networkMonitor,
						jsUrl,
				  })),
		[
			// Should all not change
			client,
			createFatalErrorHandler,
			disableImport,
			editor,
			initialContext,
			jsUrl,
			logger,
			networkMonitor,
			queryString,
			revision,
			webWorker,
		],
	);

	// Make ImporterContext available on console in 'Debug' mode
	// Assuming only one context is used, else global variable is overridden
	if (eXpress.isDebugMode) {
		(window as any)._importerContext = contextValue;
	}

	// Create React AppContext value
	return <ImporterContext.Provider value={contextValue}>{children}</ImporterContext.Provider>;
}

/** Hook to get the Importer */
export const useImporter = () => useContext(ImporterContext);
